<template>
  <div class="LocationSelector">
    <div class="Form__fieldCtn">
      <div class="mb-2">{{ $t('inputs.locationSelector.searchAddress') }}</div>
      <Geocoding
        :search-type="NominatimSearchType.FREE"
        :osm-type="OsmType.NODE"
        v-model="geoData"
        :error-messages="errorMessages"
      />
    </div>
    <Divider>{{ $t('forms.or') }}</Divider>
    <div class="Form__fieldCtn">
      <span class="mb-2">{{ $t('inputs.locationSelector.enterGpsCoords') }}</span>
      <CoordinatesSelector
        v-model:lat="lat"
        v-model:lng="lng"
        @update-coords="resetGeodata"
        :error-message="errorMessages"
      />
    </div>
    <span v-if="errorMessage">{{ errorMessage }}</span>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import Geocoding from '@/components/forms/Geocoding.vue'
import { NominatimSearchType } from '@/models/enums/geo/NominatimSearchType'
import { OsmType } from '@/models/enums/geo/OsmType'
import CoordinatesSelector from './CoordinatesSelector.vue'
import type { GeoData } from '@/models/interfaces/geo/GeoData'
import type { Ref } from 'vue'
import { computed } from 'vue'
import Divider from '../ui/Divider.vue'
const geoData = defineModel<GeoData | null>({
  default: () => ({
    osmId: null,
    osmType: null,
    name: null,
    latitude: null,
    longitude: null,
  }),
})

const lat: Ref<number | null | undefined> = ref(null)
const lng: Ref<number | null | undefined> = ref(null)

const props = defineProps<{
  errorMessage?: string
}>()

const errorMessages = computed(() => (props.errorMessage ? ' ' : undefined))

onMounted(async () => {
  updateCoords()
})

watch(
  () => geoData.value,
  () => {
    updateCoords()
  },
  { deep: true },
)

watch(
  () => [lat.value, lng.value],
  () => {
    if (geoData.value) {
      geoData.value = {
        ...geoData.value,
        latitude: lat.value,
        longitude: lng.value,
      }
    }
  },
)

const updateCoords = () => {
  if (!geoData.value) {
    return
  }
  lat.value = geoData.value?.latitude ?? geoData.value?.coords?.lat ?? null
  lng.value = geoData.value?.longitude ?? geoData.value?.coords?.lng ?? null
}

const resetGeodata = () => {
  if (!geoData.value) {
    geoData.value = {
      osmId: null,
      osmType: null,
      name: '',
      latitude: lat.value,
      longitude: lng.value,
    }
    return
  }

  geoData.value.osmId = null
  geoData.value.osmType = null
  geoData.value.name = ''
}
</script>

<style lang="scss">
.LocationSelector {
  display: flex;
  flex-flow: column nowrap;
  gap: 1rem;
}
</style>
